import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import ReactGA from 'react-ga4';
import { Link } from 'react-router-dom';

const CookieOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${props => props.show ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const CookiePopup = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 600px;
  padding: 2rem;
  max-height: 80vh;
  overflow-y: auto;
`;

const CookieTitle = styled.h2`
  text-align: center;
  margin-top: 0;
  margin-bottom: 1.5rem;
  color: #333;
  font-weight: 600;
`;

const CookieText = styled.div`
  margin-bottom: 1.5rem;
  line-height: 1.6;
  color: #555;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  margin-top: 1.5rem;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  padding: 0.8rem 1.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  font-size: 0.95rem;
  
  &.configure {
    background-color: #f8f9fa;
    color: #333;
    border: 1px solid #ddd;
    
    &:hover {
      background-color: #e9ecef;
    }
  }
  
  &.reject {
    background-color: #f8f9fa;
    color: #333;
    border: 1px solid #ddd;
    
    &:hover {
      background-color: #e9ecef;
    }
  }
  
  &.accept {
    background-color: #007bff;
    color: white;
    border: none;
    
    &:hover {
      background-color: #0056b3;
    }
  }
`;

const CookieSettings = styled.div`
  display: ${props => props.show ? 'block' : 'none'};
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #eee;
`;

const CookieOption = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f5f5f5;
  
  &:last-child {
    margin-bottom: 0;
    border-bottom: none;
  }
`;

const CookieOptionText = styled.div`
  flex: 1;
  
  h4 {
    margin: 0 0 0.5rem 0;
    font-size: 1rem;
  }
  
  p {
    margin: 0;
    font-size: 0.9rem;
    color: #666;
  }
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
  margin-left: 1rem;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: .4s;
    border-radius: 24px;
  }
  
  span:before {
    position: absolute;
    content: "";
    height: 16px;
    width: 16px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: .4s;
    border-radius: 50%;
  }
  
  input:checked + span {
    background-color: #007bff;
  }
  
  input:checked + span:before {
    transform: translateX(26px);
  }
  
  input:disabled + span {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

const CookieConsent = () => {
  const [showConsent, setShowConsent] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookieOptions, setCookieOptions] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });
  
  useEffect(() => {
    // Comprobar si el usuario ya ha dado su consentimiento
    const consent = localStorage.getItem('cookieConsent');
    if (consent === null) {
      // Si no hay consentimiento guardado, mostrar el aviso
      setShowConsent(true);
    } else if (consent === 'accepted' || consent === 'configured') {
      // Si el usuario aceptó, inicializar Google Analytics si está habilitado
      const options = JSON.parse(localStorage.getItem('cookieOptions') || '{"analytics":true}');
      if (options.analytics) {
        ReactGA.initialize('G-B47NBJBGWX');
      }
    }
  }, []);
  
  const handleAcceptAll = () => {
    // Guardar el consentimiento y activar todas las cookies
    localStorage.setItem('cookieConsent', 'accepted');
    const allOptions = { necessary: true, analytics: true, marketing: true };
    localStorage.setItem('cookieOptions', JSON.stringify(allOptions));
    setCookieOptions(allOptions);
    setShowConsent(false);
    
    // Inicializar Google Analytics
    ReactGA.initialize('G-B47NBJBGWX');
    
    // Registrar el evento de aceptación
    ReactGA.event({
      category: 'Cookies',
      action: 'Aceptadas todas',
    });
  };
  
  const handleRejectAll = () => {
    // Guardar el rechazo, solo mantener cookies necesarias
    localStorage.setItem('cookieConsent', 'rejected');
    const necessaryOnly = { necessary: true, analytics: false, marketing: false };
    localStorage.setItem('cookieOptions', JSON.stringify(necessaryOnly));
    setCookieOptions(necessaryOnly);
    setShowConsent(false);
  };
  
  const handleConfigure = () => {
    setShowSettings(!showSettings);
  };
  
  const handleOptionChange = (option) => {
    const newOptions = {
      ...cookieOptions,
      [option]: !cookieOptions[option]
    };
    setCookieOptions(newOptions);
  };
  
  const handleSaveSettings = () => {
    localStorage.setItem('cookieConsent', 'configured');
    localStorage.setItem('cookieOptions', JSON.stringify(cookieOptions));
    setShowConsent(false);
    
    // Inicializar Google Analytics solo si está habilitado
    if (cookieOptions.analytics) {
      ReactGA.initialize('G-B47NBJBGWX');
      
      // Registrar el evento de configuración
      ReactGA.event({
        category: 'Cookies',
        action: 'Configuradas',
        label: `Analytics: ${cookieOptions.analytics}, Marketing: ${cookieOptions.marketing}`
      });
    }
  };
  
  return (
    <CookieOverlay show={showConsent}>
      <CookiePopup>
        <CookieTitle>POLÍTICA SOBRE COOKIES</CookieTitle>
        <CookieText>
          <p>JuanFra Hernández, utiliza cookies y otras tecnologías similares, propias y de terceros para fines funcionales (permitiendo la navegación en la web) y analíticos (conocer el funcionamiento y uso de la web para su optimización), así como para mostrarte publicidad (tanto general como personalizada, en base a un perfil elaborado a partir de tus hábitos de navegación p.ej. páginas visitadas) y para poder valorar las opiniones de los productos adquiridos por los usuarios.</p>
          <p>Para administrar o deshabilitar estas cookies haz clic en configurar cookies <Link to="/politica-cookies" style={{ color: '#007bff', textDecoration: 'underline' }}>Política de cookies</Link>.</p>
        </CookieText>
        
        <ButtonGroup>
          <Button className="configure" onClick={handleConfigure}>
            Configurar cookies
          </Button>
          <Button className="reject" onClick={handleRejectAll}>
            Rechazar todas
          </Button>
          <Button className="accept" onClick={handleAcceptAll}>
            Aceptar todas
          </Button>
        </ButtonGroup>
        
        <CookieSettings show={showSettings}>
          <h3>Configuración de cookies</h3>
          
          <CookieOption>
            <CookieOptionText>
              <h4>Cookies necesarias</h4>
              <p>Estas cookies son necesarias para el funcionamiento básico del sitio web y no pueden ser desactivadas.</p>
            </CookieOptionText>
            <Toggle>
              <input 
                type="checkbox" 
                checked={cookieOptions.necessary} 
                disabled={true}
              />
              <span></span>
            </Toggle>
          </CookieOption>
          
          <CookieOption>
            <CookieOptionText>
              <h4>Cookies analíticas</h4>
              <p>Estas cookies nos permiten medir y analizar la forma en que los usuarios navegan por el sitio web, ayudándonos a mejorar nuestro servicio.</p>
            </CookieOptionText>
            <Toggle>
              <input 
                type="checkbox" 
                checked={cookieOptions.analytics} 
                onChange={() => handleOptionChange('analytics')}
              />
              <span></span>
            </Toggle>
          </CookieOption>
          
          <CookieOption>
            <CookieOptionText>
              <h4>Cookies de marketing</h4>
              <p>Estas cookies se utilizan para rastrear a los visitantes en los sitios web. La intención es mostrar anuncios relevantes y atractivos para el usuario individual.</p>
            </CookieOptionText>
            <Toggle>
              <input 
                type="checkbox" 
                checked={cookieOptions.marketing} 
                onChange={() => handleOptionChange('marketing')}
              />
              <span></span>
            </Toggle>
          </CookieOption>
          
          <ButtonGroup>
            <Button className="accept" onClick={handleSaveSettings}>
              Guardar preferencias
            </Button>
          </ButtonGroup>
        </CookieSettings>
      </CookiePopup>
    </CookieOverlay>
  );
};

export default CookieConsent;